<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import LiveMap from '@/Components/GPS/LiveMap.vue'
import AlertHistory from '@/Components/GPS/AlertHistory.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  rental:   { type: Object, required: true },
  route:    { type: Array,  default: () => [] },
  position: { type: Object, default: null },
  alerts:   { type: Array,  default: () => [] },
})

const { formatDateTime } = useCurrency()
const livePosition = ref(props.position)
let pollInterval   = null

const isActive  = computed(() => props.rental.status === 'active')
const mapMode   = computed(() => props.route.length > 0 ? 'route' : 'position')
const vehicleAsArray = computed(() => [{
  id:                props.rental.id,
  make:              props.rental.vehicle_make,
  model:             props.rental.vehicle_model,
  registration:      props.rental.vehicle_reg,
  status:            props.rental.status,
  traccar_device_id: null,
}])

async function fetchPosition() {
  if (!isActive.value) return
  try {
    const res  = await fetch('/gps/positions', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const data = await res.json()
    if (data.positions?.length) livePosition.value = data.positions[0]
  } catch { /* keep last known */ }
}

onMounted(() => { if (isActive.value) pollInterval = setInterval(fetchPosition, 15_000) })
onBeforeUnmount(() => { clearInterval(pollInterval) })
</script>

<template>
  <div class="p-6 max-w-screen-xl mx-auto space-y-6">
    <div class="flex items-center gap-3">
      <a :href="`/rentals/${rental.id}`" class="text-sm text-[color:var(--accent)] hover:underline">
        ← Rental #{{ rental.id }}
      </a>
      <span class="text-[color:var(--muted)]">/</span>
      <span class="text-sm font-medium text-[color:var(--inkSoft)]">GPS Tracking</span>
    </div>

    <div class="bg-[color:var(--surface)] rounded-[var(--radius-lg)] border border-[color:var(--line)] p-5">
      <dl class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div>
          <dt class="text-[color:var(--muted)]">Customer</dt>
          <dd class="font-medium text-[color:var(--ink)] mt-0.5">{{ rental.customer_name }}</dd>
        </div>
        <div>
          <dt class="text-[color:var(--muted)]">Vehicle</dt>
          <dd class="font-medium text-[color:var(--ink)] mt-0.5">{{ rental.vehicle_make }} {{ rental.vehicle_model }}</dd>
        </div>
        <div>
          <dt class="text-[color:var(--muted)]">Start</dt>
          <dd class="font-medium text-[color:var(--ink)] mt-0.5">{{ formatDateTime(rental.start_at) }}</dd>
        </div>
        <div>
          <dt class="text-[color:var(--muted)]">Expected Return</dt>
          <dd class="font-medium text-[color:var(--ink)] mt-0.5">{{ formatDateTime(rental.expected_end_at) }}</dd>
        </div>
      </dl>
    </div>

    <div
      v-if="!rental.has_tracker"
      class="rounded-[var(--radius-md)] px-4 py-3 text-sm"
      :style="{ background: 'var(--warnSoft)', border: '1px solid color-mix(in oklab, var(--warn) 35%, transparent)', color: 'var(--warn)' }"
    >
      This vehicle does not have a GPS tracker linked. Go to the vehicle's GPS page to link one.
    </div>

    <template v-else>
      <div>
        <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-2">
          {{ mapMode === 'route' ? 'Trip Route' : 'Current Position' }}
        </h2>
        <div class="-mx-6 md:mx-0">
          <LiveMap
            :route="route"
            :positions="livePosition && isActive ? [livePosition] : []"
            :vehicles="vehicleAsArray"
            :zoom="13"
            class="!h-[60vh] md:!h-[500px] md:!rounded-[var(--radius-lg)]"
          />
        </div>
      </div>
    </template>

    <AlertHistory :alerts="alerts" />
  </div>
</template>
